import { useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import { describeApiError } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";
import { uploadPhoto, useAdminMedia } from "../../lib/queries";
import { PhotoPicker } from "../../components/PhotoPicker";
import { Modal } from "../../components/Modal";
import { Spinner } from "../../components/Spinner";
import type { AuditEvent, MediaKind } from "../../types";

/** Busy / error / notice state shared by every admin form. */
export function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const run = async (key: string, work: () => Promise<string | void>) => {
    setBusy(key); setError(null); setNotice(null);
    try {
      const message = await work();
      setNotice(message ?? "Cambios guardados.");
      return true;
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos guardar los cambios."));
      return false;
    } finally { setBusy(null); }
  };
  return { busy, error, notice, run, clear: () => { setError(null); setNotice(null); } };
}

export function Feedback({ error, notice }: { error: string[] | null; notice: string | null }) {
  return (
    <>
      {notice && <div className="form-message" role="status">{notice}</div>}
      {error?.length ? (
        <div className="form-error" role="alert">
          {error.length === 1 ? error[0] : <ul className="form-error-list">{error.map((message) => <li key={message}>{message}</li>)}</ul>}
        </div>
      ) : null}
    </>
  );
}

export function Field({ label, htmlFor, hint, children }: { label: string; htmlFor?: string; hint?: string; children: ReactNode }) {
  return (
    <div className="field-block">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

/** Chooses a catalogue photo of a kind (uploading a new one if needed). */
export function AdminPhotoField({ kind, value, onChange, label = "Foto" }: { kind: MediaKind; value: string | null; onChange(id: string | null): void; label?: string }) {
  const media = useAdminMedia(kind);
  const [expanded, setExpanded] = useState(false);
  const current = media.data?.find((photo) => photo.id === value);
  return (
    <div className="field-block">
      <span className="field-label">{label}</span>
      <div className="admin-photo-field">
        <div className="admin-photo-current" style={current ? { backgroundImage: `url(${current.url})` } : undefined}>{!current && <span>Sin foto</span>}</div>
        <div className="admin-photo-copy">
          <strong>{current?.title ?? (value ? "Foto seleccionada" : "Ninguna")}</strong>
          <button type="button" className="link-button" onClick={() => setExpanded((open) => !open)}>{expanded ? "Ocultar catálogo" : "Cambiar foto"}</button>
        </div>
      </div>
      {expanded && (
        <PhotoPicker
          photos={media.data ?? []}
          value={value}
          onChange={(id) => { onChange(id); void media.refetch(); }}
          allowNone
          onUpload={async (file) => { const photo = await uploadPhoto(file, { admin: true, kind, title: file.name.replace(/\.[^.]+$/, "") }); await media.refetch(); return photo; }}
        />
      )}
    </div>
  );
}

const ACTION_LABELS: Record<string, string> = {
  "auth.register": "Registro", "auth.login": "Inicio de sesión", "auth.step_up": "Confirmación de contraseña",
  "wallet.create.requested": "Solicitud de wallet", "wallet.create.confirmed": "Wallet confirmada", "wallet.deposit.requested": "Aportación solicitada",
  "wallet.deposit.provider_ack": "Aportación confirmada", "wallet.withdrawal.requested": "Retiro solicitado", "wallet.withdrawal.provider_ack": "Retiro confirmado",
  "wallet.goal.update": "Sueño actualizado", "wallet.reconcile": "Conciliación (histórico)", "wallet.funding.viewed": "CLABE consultada",
  "wallet.balance.synced": "Saldo leído de Kuspit", "wallet.balance.refreshed": "Saldo actualizado por el cliente",
  "admin.user.update": "Perfil editado por admin", "admin.trip.update": "Viaje editado por admin", "admin.wallet.update": "Wallet editada por admin",
  "admin.user.close": "Cuenta cerrada por admin", "admin.user.purge": "Cuenta eliminada por admin", "admin.wallet.close": "Wallet cerrada por admin", "admin.wallet.purge": "Wallet eliminada por admin",
  "admin.media.upload": "Foto subida", "admin.media.update": "Foto editada", "admin.media.delete": "Foto eliminada",
  "admin.region.create": "Región creada", "admin.region.update": "Región editada", "admin.region.delete": "Región eliminada",
  "admin.destination.create": "Destino creado", "admin.destination.update": "Destino editado", "admin.destination.delete": "Destino eliminado",
  "admin.product.update": "Producto editado", "admin.settings.update": "Ajustes editados", "media.upload": "Foto del cliente subida",
  "profile.update": "Perfil actualizado", "profile.spei_account.added": "Cuenta SPEI agregada",
  "loan.document.upload": "Constancia fiscal subida", "loan.document.delete": "Constancia fiscal eliminada", "loan.document.view": "Constancia consultada por el aliado",
  "loan.application.submit": "Solicitud de crédito enviada", "loan.application.review": "Solicitud tomada en revisión",
  "loan.application.approved": "Solicitud de crédito aprobada", "loan.application.rejected": "Solicitud de crédito rechazada",
  "admin.lender.create": "Acceso de aliado creado", "admin.lender.reset_password": "Contraseña de aliado restablecida",
  "admin.kuspit.probe": "Túnel Kuspit probado por admin",
  "admin.kuspit.provision": "Aprovisionamiento Kuspit reintentado por admin", "admin.kuspit.relogin": "Revinculación Kuspit por admin",
  "admin.kuspit.notification.retry": "Webhook Kuspit reencolado por admin",
  "admin.kuspit.adjustment": "Ajuste de conciliación registrado por admin (histórico)", "admin.kuspit.review": "Discrepancia revisada por admin (histórico)",
  "admin.kuspit.synchronize": "Saldo de Kuspit consultado por admin", "wallet.reconcile.automated": "Conciliación automática (histórico)", "wallet.create.recovered": "Wallet recuperada por el worker", "wallet.create.rejected": "Apertura rechazada por Kuspit",
  "kuspit.notification.deposit": "Depósito SPEI aplicado", "kuspit.notification.contract_authorized": "Contrato autorizado por Kuspit",
  "wallet.interest.posted": "Rendimiento abonado", "auth.password_reset.request": "Recuperación de contraseña solicitada", "auth.password_reset.confirm": "Contraseña restablecida",
  "auth.logout": "Cierre de sesión", "profile.password.change": "Contraseña cambiada", "profile.sessions.revoke": "Sesiones cerradas"
};

/** Kuspit webhook event codes (catalogue of the notification service), in Spanish for the panel. */
export const KUSPIT_EVENT_LABELS: Record<string, string> = {
  "01": "Depósito recibido", "02": "Depósito rechazado", "03": "Documentos validados", "04": "Documentos rechazados", "05": "Expediente completo",
  "06": "Transferencia exitosa", "07": "Transferencia fallida", "08": "Contrato autorizado", "09": "Devolución", "10": "Devolución interna", "11": "Contrato no autorizado"
};

export function describeKuspitEvent(code: string): string {
  return KUSPIT_EVENT_LABELS[code] ? `${code} · ${KUSPIT_EVENT_LABELS[code]}` : `Evento ${code}`;
}

/** Spanish labels for the status vocabularies the panels show as pills. */
export const STATUS_LABELS: Record<string, string> = {
  active: "Activa", pending: "Pendiente", suspended: "Suspendida", closed: "Cerrada",
  received: "En cola", processed: "Aplicado", ignored: "Ignorado", failed: "Fallido",
  synced: "Al día", stale: "Desactualizado", never: "Sin leer",
  running: "En curso", matched: "Cuadra", mismatched: "Descuadre",
  verified: "Verificada", rejected: "Rechazada", customer: "Cliente", agent: "Agente", admin: "Administrador", lender: "Aliado",
  submitted: "Nueva", in_review: "En revisión", approved: "Aprobada"
};

const STATUS_TONES: Record<string, string> = {
  active: "ok", processed: "ok", matched: "ok", synced: "ok", verified: "ok", approved: "ok",
  pending: "warn", received: "warn", running: "warn", never: "warn", stale: "warn", submitted: "warn", in_review: "info",
  suspended: "bad", closed: "bad", failed: "bad", mismatched: "bad", rejected: "bad", ignored: "muted"
};

/** A compact status pill; the colour follows the meaning, the text always says it too. */
export function Pill({ status, label, tone }: { status?: string; label?: string; tone?: "ok" | "warn" | "bad" | "info" | "muted" }) {
  const resolved = tone ?? (status ? STATUS_TONES[status] ?? "muted" : "muted");
  return <span className={`pill ${resolved}`}>{label ?? (status ? STATUS_LABELS[status] ?? status : "")}</span>;
}

/** One KPI. `tone` colours the icon; `to` makes the tile a link to the screen that explains the number. */
export function StatTile({ label, value, hint, icon: Icon, to, tone = "default", compact = false }: {
  label: string; value: ReactNode; hint?: ReactNode; icon?: LucideIcon; to?: string; tone?: "default" | "alert" | "danger" | "ok"; compact?: boolean;
}) {
  const body = (
    <>
      {Icon && <span className={`benefit-icon ${tone}`}><Icon size={17} aria-hidden="true" /></span>}
      <span className="stat-label">{label}</span>
      <strong translate="no">{value}</strong>
      {hint && <small>{hint}</small>}
    </>
  );
  const className = `stat-tile${compact ? " compact" : ""}${tone !== "default" ? ` ${tone}` : ""}`;
  return to ? <Link className={className} to={to}>{body}</Link> : <div className={className}>{body}</div>;
}

/** Section tabs for a multi-page panel (Kuspit, Peso a Peso). */
export function SubNav({ items, ariaLabel }: { items: Array<{ to: string; label: string; end?: boolean; count?: number | null }>; ariaLabel: string }) {
  return (
    <nav className="subnav" aria-label={ariaLabel}>
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end} className={({ isActive }) => `subnav-item${isActive ? " active" : ""}`}>
          {item.label}{item.count ? <span className="subnav-count">{item.count}</span> : null}
        </NavLink>
      ))}
    </nav>
  );
}

/** Label/value rows for a detail card. */
export function KeyValues({ rows, className = "" }: { rows: Array<{ label: string; value: ReactNode; mono?: boolean } | null | false>; className?: string }) {
  return (
    <dl className={`kv-list ${className}`}>
      {rows.filter((row): row is { label: string; value: ReactNode; mono?: boolean } => Boolean(row)).map((row) => (
        <div key={row.label}><dt>{row.label}</dt><dd className={row.mono ? "mono" : undefined} translate="no">{row.value}</dd></div>
      ))}
    </dl>
  );
}

export function EmptyRow({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return <tr><td colSpan={colSpan} className="table-empty">{children}</td></tr>;
}

export function PageError({ title, retry }: { title: string; retry(): void }) {
  return <div className="page-state error"><strong>{title}</strong><button type="button" className="secondary-button" onClick={retry}>Reintentar</button></div>;
}

/** Amount formatter for tiles: whole pesos above 10k so the tile stays on one line. */
export const compactMoney = (value: number) =>
  Math.abs(value) >= 10_000
    ? new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(value)
    : new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 2 }).format(value);

export const percent = (value: number | null | undefined, digits = 0) => (value === null || value === undefined ? "—" : `${(value * 100).toFixed(digits)}%`);

export function hoursLabel(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return "—";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${hours.toFixed(hours < 10 ? 1 : 0)} h`;
  return `${(hours / 24).toFixed(1)} días`;
}

export function describeAction(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

export function AuditList({ events, empty = "Sin actividad registrada." }: { events: AuditEvent[]; empty?: string }) {
  if (!events.length) return <p className="rail-empty">{empty}</p>;
  return (
    <div className="audit-list">
      {events.map((event) => (
        <div className="audit-row" key={event.id}>
          <span className={`audit-dot ${event.outcome}`} aria-hidden="true" />
          <div>
            <strong>{describeAction(event.action)}</strong>
            <span>{event.actorType} · {formatTimestamp(event.createdAt)}{event.targetType ? ` · ${event.targetType}` : ""}</span>
          </div>
          <code>{event.action}</code>
        </div>
      ))}
    </div>
  );
}

/**
 * A destructive-action button that requires a confirmation in a modal before it runs. `onConfirm`
 * does the work (API call + cache invalidation + any navigation); an error keeps the modal open and
 * shows the reason, so a guard rejection from the server (funds present, live contract) is legible.
 */
export function ConfirmButton({
  label, title, message, confirmLabel, onConfirm, danger = false, disabled = false, disabledHint, icon
}: {
  label: string;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  onConfirm(): Promise<void>;
  danger?: boolean;
  disabled?: boolean;
  disabledHint?: string;
  icon?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string[] | null>(null);

  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await onConfirm();
      setOpen(false);
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos completar la acción."));
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        type="button"
        className={danger ? "danger-button" : "secondary-button"}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        onClick={() => { setError(null); setOpen(true); }}
      >
        {icon}{label}
      </button>
      <Modal open={open} label={title} onOpenChange={(next) => { if (!busy) setOpen(next); }}>
        <div className="dialog-content">
          <h2>{title}</h2>
          <div className="dialog-body">{message}</div>
          <Feedback error={error} notice={null} />
          <div className="dialog-actions">
            <button type="button" className="secondary-button" onClick={() => setOpen(false)} disabled={busy}>Cancelar</button>
            <button type="button" className={danger ? "danger-button solid" : "primary-button solid"} onClick={() => void confirm()} disabled={busy}>
              {busy ? <Spinner /> : null} {confirmLabel}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
